import React from "react";
import {
    Box,
    Typography,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionIcon from "@mui/icons-material/Description";
import FunctionsIcon from "@mui/icons-material/Functions";
import ErrorIcon from "@mui/icons-material/Error";
import { FunctionCallsArray } from "./types";
import AcxDialog from "../../../../UI/Dialog/AcxDialog";
import AcxChip from "../../../../UI/AcxChip";

interface Props {
    open: boolean;
    onClose: () => void;
    functionCalls: FunctionCallsArray | undefined;
}

const FunctionCallsModal: React.FC<Props> = ({ open, onClose, functionCalls }) => {
    const formatArguments = (args: Record<string, any>): Record<string, any> | null => {
        if (!args) return null;
        
        try {
            // If args is a string, try to parse it
            if (typeof args === 'string') {
                return JSON.parse(args);
            }
            return args;
        } catch (e) {
            return args as Record<string, any>;
        }
    };

    const renderArgumentValue = (value: unknown): string => {
        if (typeof value === 'string') {
            try {
                // Try to parse if it's JSON string
                const parsed = JSON.parse(value);
                return JSON.stringify(parsed, null, 2);
            } catch {
                return value;
            }
        }
        return JSON.stringify(value, null, 2);
    };

    const getTitle = () => {
        if (!functionCalls || !Array.isArray(functionCalls)) {
            return "Tool Calls";
        }
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FunctionsIcon color="primary" />
                <span>Tool Calls</span>
                <AcxChip 
                    label={`${functionCalls.length} call${functionCalls.length !== 1 ? 's' : ''}`} 
                    size="small" 
                    color="lightBlue"
                    style={{ marginLeft: 8 }}
                />
            </Box>
        );
    };

    return (
        <AcxDialog 
            isOpen={open} 
            onClose={onClose} 
            maxWidth="md" 
            title={getTitle()}
            contentWidth="800px"
            dialogContentChildren={
                functionCalls && Array.isArray(functionCalls) ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {functionCalls.map((call, index) => {
                            const functionInfo = call.Function || {};
                            const metadata = functionInfo.Metadata || {};
                            const args = formatArguments(call.Arguments);
                            
                            return (
                                <Accordion key={index} defaultExpanded={functionCalls.length === 1}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                                            <Typography variant="subtitle1" fontWeight="medium">
                                                {metadata.Name || functionInfo.Name || "Unknown Function"}
                                            </Typography>
                                            {metadata.PluginName && (
                                                <AcxChip 
                                                    label={metadata.PluginName} 
                                                    size="small" 
                                                    color="lightBlue"
                                                />
                                            )}
                                            {call.Exception && (
                                                <AcxChip 
                                                    label="Exception" 
                                                    size="small" 
                                                    color="red"
                                                    customMaterialIcon={<ErrorIcon />}
                                                />
                                            )}
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            {/* Exception */}
                                            {call.Exception && (
                                                <Box>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                        <ErrorIcon fontSize="small" color="error" />
                                                        <Typography variant="body2" fontWeight="medium" color="error">
                                                            Exception Stack Trace
                                                        </Typography>
                                                    </Box>
                                                    <Paper 
                                                        variant="outlined" 
                                                        sx={{ 
                                                            p: 2, 
                                                            backgroundColor: "error.50",
                                                            borderColor: "error.main"
                                                        }}
                                                    >
                                                        <Box
                                                            component="pre"
                                                            sx={{
                                                                margin: 0,
                                                                fontSize: "0.875rem",
                                                                fontFamily: "monospace",
                                                                whiteSpace: "pre-wrap",
                                                                wordBreak: "break-word",
                                                                color: "error.dark"
                                                            }}
                                                        >
                                                            {call.Exception}
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                            )}
                                            
                                            {/* Description */}
                                            {(metadata.Description || functionInfo.Description) && (
                                                <Box>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                        <DescriptionIcon fontSize="small" color="action" />
                                                        <Typography variant="body2" fontWeight="medium">
                                                            Description
                                                        </Typography>
                                                    </Box>
                                                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: "grey.50" }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {metadata.Description || functionInfo.Description}
                                                        </Typography>
                                                    </Paper>
                                                </Box>
                                            )}
                                            
                                            {/* Arguments */}
                                            {args && Object.keys(args).length > 0 && (
                                                <Box>
                                                    <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                                        Arguments
                                                    </Typography>
                                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                                        {Object.entries(args).map(([key, value]) => (
                                                            <Box key={key} sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
                                                                <Typography variant="body2" fontWeight="medium" color="primary">
                                                                    {key}:
                                                                </Typography>
                                                                <Box
                                                                    component="pre"
                                                                    sx={{
                                                                        backgroundColor: "grey.100",
                                                                        p: 1,
                                                                        mt: 0.5,
                                                                        borderRadius: 1,
                                                                        overflow: "auto",
                                                                        fontSize: "0.875rem",
                                                                        fontFamily: "monospace",
                                                                        whiteSpace: "pre-wrap",
                                                                        wordBreak: "break-word",
                                                                        maxHeight: 200,
                                                                    }}
                                                                >
                                                                    {renderArgumentValue(value)}
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Paper>
                                                </Box>
                                            )}
                                            
                                            {/* Parameters Info */}
                                            {metadata.Parameters && metadata.Parameters.length > 0 && (
                                                <Box>
                                                    <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                                        Parameter Details
                                                    </Typography>
                                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                                        {metadata.Parameters.map((param: any, paramIndex: number) => (
                                                            <Box key={paramIndex} sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}>
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                    <Typography variant="body2" fontWeight="medium">
                                                                        {param.Name}
                                                                    </Typography>
                                                                    {param.IsRequired && (
                                                                        <AcxChip label="Required" size="small" color="red" />
                                                                    )}
                                                                </Box>
                                                                {param.Description && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {param.Description}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        ))}
                                                    </Paper>
                                                </Box>
                                            )}
                                            
                                            {/* Raw JSON (collapsed by default) */}
                                            <Accordion>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        View Raw JSON
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Box
                                                        component="pre"
                                                        sx={{
                                                            backgroundColor: "grey.100",
                                                            p: 2,
                                                            borderRadius: 1,
                                                            overflow: "auto",
                                                            fontSize: "0.75rem",
                                                            fontFamily: "monospace",
                                                            whiteSpace: "pre-wrap",
                                                            wordBreak: "break-word",
                                                            maxHeight: 300,
                                                        }}
                                                    >
                                                        {JSON.stringify(call, null, 2)}
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No function calls to display
                    </Typography>
                )
            }
            hideCancelButton={true}
        />
    );
};

export default FunctionCallsModal;